import { useState, useRef, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';
import { parseDateOnly } from '../../../utils/dateOnly';

function parseValue(v) {
  if (!v) return { start: '', end: '' };
  if (typeof v === 'string') return { start: v, end: '' };
  return { start: v.start || '', end: v.end || '' };
}

function fmt(d) {
  if (!d) return '';
  const date = parseDateOnly(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = parseDateOnly(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

export default function DateCell({ value, column, onChange, readOnly = false }) {
  // config.mode === 'single' ("Data de vencimento"): uma data só, gravada como string simples.
  // Sem esse config a coluna é de intervalo (início e fim opcional), como sempre foi.
  const single = column?.config?.mode === 'single';
  const { start, end: parsedEnd } = parseValue(value);
  const end = single ? '' : parsedEnd;
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState(start);
  const [localEnd, setLocalEnd] = useState(end);
  const ref = useRef(null);

  useEffect(() => {
    const p = parseValue(value);
    setLocalStart(p.start);
    setLocalEnd(single ? '' : p.end);
  }, [value, single]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) commit(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, localStart, localEnd]);

  const commit = () => {
    setOpen(false);
    const ns = localStart || null;
    const ne = single ? null : (localEnd || null);
    if (!ns && !ne) { if (value) onChange(null); return; }
    if (!ne) { if (value !== ns && !(typeof value === 'object' && value?.start === ns && !value?.end)) onChange(ns); return; }
    const next = { start: ns, end: ne };
    const same = typeof value === 'object' && value?.start === ns && value?.end === ne;
    if (!same) onChange(next);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setLocalStart('');
    setLocalEnd('');
    onChange(null);
    setOpen(false);
  };

  const hasAny = start || end;
  const isRange = !!(start && end);
  const daysStart = daysUntil(start);
  const overdue = daysStart != null && daysStart < 0;
  const today = daysStart === 0;

  return (
    <div ref={ref} className="relative w-full h-full">
      <div
        onClick={() => { if (!readOnly) setOpen(!open); }}
        className={`w-full h-full flex items-center justify-center gap-1.5 px-2 text-sm transition-colors min-h-[40px] group/d ${readOnly ? '' : 'cursor-pointer hover:bg-white/5'}`}
      >
        {hasAny ? (
          <>
            {isRange ? (
              <span className="text-xs font-medium text-text-primary bg-accent/15 px-2 py-0.5 rounded-full whitespace-nowrap">
                {fmt(start)} – {fmt(end)}
              </span>
            ) : (
              <span className={`text-sm ${overdue ? 'text-danger' : today ? 'text-warning' : 'text-text-primary'}`}>
                {fmt(start)}
              </span>
            )}
            {!readOnly && (
              <button
                onClick={handleClear}
                className="opacity-0 group-hover/d:opacity-100 text-text-secondary hover:text-danger transition-opacity"
                title="Limpar"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </>
        ) : (
          <Calendar className="w-3.5 h-3.5 text-text-secondary/50" />
        )}
      </div>

      {open && !readOnly && (
        <div className="absolute z-50 top-full left-0 mt-1 w-[260px] bg-card border border-border rounded-lg shadow-xl p-3">
          <label className="block text-[11px] text-text-secondary mb-1">{single ? 'Data' : 'Data inicial'}</label>
          <input
            type="date"
            value={localStart}
            onChange={(e) => setLocalStart(e.target.value)}
            className={`w-full bg-main border border-border rounded px-2 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50 [color-scheme:dark]${single ? '' : ' mb-2'}`}
          />
          {!single && (
            <>
              <label className="block text-[11px] text-text-secondary mb-1">Data final (opcional)</label>
              <input
                type="date"
                value={localEnd}
                onChange={(e) => setLocalEnd(e.target.value)}
                min={localStart || undefined}
                className="w-full bg-main border border-border rounded px-2 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50 [color-scheme:dark]"
              />
            </>
          )}
          <div className="mt-3 flex justify-between">
            <button
              onClick={handleClear}
              className="text-xs text-text-secondary hover:text-danger transition-colors"
            >
              Limpar
            </button>
            <button
              onClick={commit}
              className="text-xs bg-accent text-white px-3 py-1 rounded hover:bg-accent/80 transition-colors"
            >
              Concluído
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
